import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, computed, signal } from '@angular/core';
import { GoogleMap, MapInfoWindow, MapMarker } from '@angular/google-maps';
import { ApproachingBusDto } from '@buspro/web/apis';

@Component({
  selector: 'buspro-admin-bus-location-map',
  standalone: true,
  imports: [CommonModule, GoogleMap, MapMarker, MapInfoWindow],
  template: `
    <google-map
      [center]="mapCenter()"
      [zoom]="zoomSignal()"
      [options]="mapOptions"
      class="w-full h-full"
      (mapClick)="selectedBus.set(null)"
    >
      <!-- Bus Markers -->
      @for (bus of busesSignal(); track bus.operationId) {
        <map-marker
          [position]="getBusPosition(bus)"
          [options]="getBusMarkerOptions(bus)"
          (mapClick)="onBusClick(bus)"
        >
        </map-marker>
      }

      <!-- Location Marker (if provided) -->
      @if (locationCenterSignal()) {
        <map-marker
          [position]="locationCenterSignal()!"
          [options]="locationMarkerOptions"
        >
        </map-marker>
      }

      <!-- Info Window -->
      @if (selectedBus()) {
        <map-info-window [position]="getBusPosition(selectedBus()!)">
          <div class="p-2">
            <h4 class="font-semibold text-lg mb-2">{{ selectedBus()?.busAssetName }}</h4>
            <p class="text-sm text-gray-600 mb-1">{{ selectedBus()?.routeName }}</p>
            <p class="text-sm font-semibold text-primary mb-1">
              ETA: {{ selectedBus()?.estimatedArrivalMinutes }} minutes
            </p>
            <p class="text-sm text-gray-600">
              Available: {{ selectedBus()?.availableSeats }}/{{ selectedBus()?.totalSeats }} seats
            </p>
            <button
              class="btn btn-primary btn-sm mt-2 w-full"
              (click)="bookSeat.emit(selectedBus()!); $event.stopPropagation()"
              [disabled]="selectedBus()?.availableSeats === 0"
            >
              Book Seat
            </button>
          </div>
        </map-info-window>
      }
    </google-map>
  `,
  styles: [
    `
      :host {
        display: block;
        width: 100%;
        height: 100%;
      }
    `,
  ],
})
export class BusLocationMapComponent implements OnChanges {
  @Input() buses: ApproachingBusDto[] = [];
  @Input() locationCenter: google.maps.LatLngLiteral | null = null;
  @Input() zoom = 13;
  @Output() bookSeat = new EventEmitter<ApproachingBusDto>();

  busesSignal = signal<ApproachingBusDto[]>([]);
  locationCenterSignal = signal<google.maps.LatLngLiteral | null>(null);
  zoomSignal = signal(13);
  selectedBus = signal<ApproachingBusDto | null>(null);

  ngOnChanges(): void {
    this.busesSignal.set(this.buses);
    this.locationCenterSignal.set(this.locationCenter);
    this.zoomSignal.set(this.zoom);
  }

  mapOptions: google.maps.MapOptions = {
    mapTypeId: 'roadmap',
    disableDefaultUI: false,
    zoomControl: true,
    streetViewControl: false,
    fullscreenControl: true,
    mapTypeControl: false,
  };

  locationMarkerOptions: google.maps.MarkerOptions = {
    icon: {
      path: google.maps.SymbolPath.CIRCLE,
      scale: 8,
      fillColor: '#3B82F6',
      fillOpacity: 1,
      strokeColor: '#FFFFFF',
      strokeWeight: 2,
    },
    title: 'Your Location',
  };

  mapCenter = computed(() => {
    const firstBus = this.busesSignal()[0];
    if (firstBus?.currentLocation) {
      return {
        lat: firstBus.currentLocation.lat,
        lng: firstBus.currentLocation.lng,
      };
    }
    if (this.locationCenterSignal()) {
      return this.locationCenterSignal()!;
    }
    // Default center (can be configured)
    return { lat: 0, lng: 0 };
  });

  getBusPosition(bus: ApproachingBusDto): google.maps.LatLngLiteral {
    if (bus.currentLocation) {
      return {
        lat: bus.currentLocation.lat,
        lng: bus.currentLocation.lng,
      };
    }
    // Fallback to route start if no location
    return { lat: 0, lng: 0 };
  }

  getBusMarkerOptions(bus: ApproachingBusDto): google.maps.MarkerOptions {
    const isSelected = this.selectedBus()?.operationId === bus.operationId;
    const hasSeats = (bus.availableSeats ?? 0) > 0 || (bus.seatForecast?.forecastAvailable ?? 0) > 0;

    const iconUrl = this.getBusIconUrl(hasSeats, isSelected);

    return {
      icon: {
        url: iconUrl,
        scaledSize: new google.maps.Size(40, 40),
        anchor: new google.maps.Point(20, 20),
      },
      title: `${bus.busAssetName} - ${bus.estimatedArrivalMinutes} min - ${bus.availableSeats} seats available`,
      animation: isSelected && typeof google !== 'undefined' && google.maps ? google.maps.Animation.BOUNCE : undefined,
    };
  }

  private getBusIconUrl(hasSeats: boolean, isSelected: boolean): string {
    // Use different colors based on seat availability and selection
    if (isSelected) {
      return (
        'data:image/svg+xml;base64,' +
        btoa(`
        <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
          <circle cx="20" cy="20" r="18" fill="#EF4444" stroke="#FFFFFF" stroke-width="3"/>
          <text x="20" y="25" font-size="20" fill="white" text-anchor="middle">🚌</text>
        </svg>
      `)
      );
    }
    if (hasSeats) {
      return (
        'data:image/svg+xml;base64,' +
        btoa(`
        <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
          <circle cx="20" cy="20" r="18" fill="#10B981" stroke="#FFFFFF" stroke-width="2"/>
          <text x="20" y="25" font-size="20" fill="white" text-anchor="middle">🚌</text>
        </svg>
      `)
      );
    }
    return (
      'data:image/svg+xml;base64,' +
      btoa(`
      <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
        <circle cx="20" cy="20" r="18" fill="#6B7280" stroke="#FFFFFF" stroke-width="2"/>
        <text x="20" y="25" font-size="20" fill="white" text-anchor="middle">🚌</text>
      </svg>
    `)
    );
  }

  onBusClick(bus: ApproachingBusDto): void {
    if (this.selectedBus()?.operationId === bus.operationId) {
      this.selectedBus.set(null);
    } else {
      this.selectedBus.set(bus);
    }
  }
}
